/**
 * Minimal CSV export utilities.
 * All functions are pure — no side effects except downloadCsv which triggers a browser download.
 */

/** Wraps a value in quotes if it contains commas, quotes, or newlines. Doubles internal quotes. */
export function csvCell(val: string | number | null | undefined): string {
  if (val === null || val === undefined) return '';
  const str = String(val);
  if (str.includes(',') || str.includes('"') || str.includes('\n') || str.includes('\r')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

/** Builds a CSV string from a header row and data rows. Uses CRLF line endings (RFC 4180). */
export function buildCsv(
  headers: string[],
  rows: (string | number | null | undefined)[][],
): string {
  const lines = [
    headers.map(csvCell).join(','),
    ...rows.map((row) => row.map(csvCell).join(',')),
  ];
  return lines.join('\r\n');
}

/** Triggers a browser file download for the given CSV content. */
export function downloadCsv(filename: string, content: string): void {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/** Returns today's date as YYYY-MM-DD for use in filenames. */
export function todayStamp(): string {
  return new Date().toISOString().slice(0, 10);
}

const GRADE_LABEL_MAP: Record<string, string> = {
  working_towards: 'Working Towards',
  expected: 'Expected',
  greater_depth: 'Greater Depth',
  WTS: 'Working Towards',
  ES: 'Expected',
  GD: 'Greater Depth',
};

/** Returns the human-readable label for a grade value, or the raw value if unrecognised. */
export function gradeLabel(val: string | null | undefined): string {
  if (!val) return '';
  return GRADE_LABEL_MAP[val] ?? val;
}

/** Formats an ISO date string as YYYY-MM-DD for spreadsheet-friendly column values. */
export function isoDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '';
  return dateStr.slice(0, 10);
}
